<template>
  <div class="block-form">
    <q-input
      outlined
      :model-value="block.title"
      label="Título (opcional)"
      @update:model-value="emit('update', { ...block, title: String($event ?? '') })"
    />

    <!-- Correct column -->
    <div class="column-section correct-section">
      <div class="column-header">
        <q-icon name="check_circle" size="16px" color="teal" />
        <span class="column-label">Columna correcta</span>
      </div>
      <q-input
        outlined
        dense
        :model-value="block.correct.label"
        label="Etiqueta (default: Correcto)"
        @update:model-value="updateCorrectLabel(String($event ?? ''))"
      />
      <div class="form-label">Items</div>
      <div
        v-for="(item, idx) in block.correct.items"
        :key="`c-${idx}`"
        class="item-row"
      >
        <q-input
          outlined
          dense
          :model-value="item"
          :label="`Item ${idx + 1}`"
          style="flex: 1;"
          @update:model-value="updateCorrectItem(idx, String($event ?? ''))"
        />
        <q-btn flat dense round icon="delete" color="negative" @click="removeCorrectItem(idx)">
          <q-tooltip>Eliminar</q-tooltip>
        </q-btn>
      </div>
      <q-btn flat no-caps icon="add" label="Agregar item" color="primary" size="sm" @click="addCorrectItem" />
    </div>

    <!-- Incorrect column -->
    <div class="column-section incorrect-section">
      <div class="column-header">
        <q-icon name="cancel" size="16px" color="negative" />
        <span class="column-label">Columna incorrecta</span>
      </div>
      <q-input
        outlined
        dense
        :model-value="block.incorrect.label"
        label="Etiqueta (default: Incorrecto)"
        @update:model-value="updateIncorrectLabel(String($event ?? ''))"
      />
      <div class="form-label">Items</div>
      <div
        v-for="(item, idx) in block.incorrect.items"
        :key="`i-${idx}`"
        class="item-row"
      >
        <q-input
          outlined
          dense
          :model-value="item"
          :label="`Item ${idx + 1}`"
          style="flex: 1;"
          @update:model-value="updateIncorrectItem(idx, String($event ?? ''))"
        />
        <q-btn flat dense round icon="delete" color="negative" @click="removeIncorrectItem(idx)">
          <q-tooltip>Eliminar</q-tooltip>
        </q-btn>
      </div>
      <q-btn flat no-caps icon="add" label="Agregar item" color="primary" size="sm" @click="addIncorrectItem" />
    </div>
  </div>
</template>

<script setup lang="ts">
interface ComparisonColumn {
  label?: string;
  items: string[];
}

interface ComparisonBlock {
  type: 'comparison';
  title?: string;
  correct: ComparisonColumn;
  incorrect: ComparisonColumn;
}

const props = defineProps<{ block: ComparisonBlock }>();
const emit = defineEmits<{ (e: 'update', block: ComparisonBlock): void }>();

function updateCorrectLabel(value: string) {
  emit('update', { ...props.block, correct: { ...props.block.correct, label: value } });
}
function updateIncorrectLabel(value: string) {
  emit('update', { ...props.block, incorrect: { ...props.block.incorrect, label: value } });
}
function updateCorrectItem(idx: number, value: string) {
  const items = [...props.block.correct.items];
  items[idx] = value;
  emit('update', { ...props.block, correct: { ...props.block.correct, items } });
}
function removeCorrectItem(idx: number) {
  const items = props.block.correct.items.filter((_, i) => i !== idx);
  emit('update', { ...props.block, correct: { ...props.block.correct, items } });
}
function addCorrectItem() {
  emit('update', { ...props.block, correct: { ...props.block.correct, items: [...props.block.correct.items, ''] } });
}
function updateIncorrectItem(idx: number, value: string) {
  const items = [...props.block.incorrect.items];
  items[idx] = value;
  emit('update', { ...props.block, incorrect: { ...props.block.incorrect, items } });
}
function removeIncorrectItem(idx: number) {
  const items = props.block.incorrect.items.filter((_, i) => i !== idx);
  emit('update', { ...props.block, incorrect: { ...props.block.incorrect, items } });
}
function addIncorrectItem() {
  emit('update', { ...props.block, incorrect: { ...props.block.incorrect, items: [...props.block.incorrect.items, ''] } });
}
</script>

<style scoped lang="scss">
.block-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}
.form-label {
  font-size: 13px;
  color: var(--oa-text-muted);
}
.item-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.column-section {
  border: 1px solid var(--oa-border);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.correct-section {
  border-color: color-mix(in srgb, var(--color-teal-wave) 30%, transparent);
  background: color-mix(in srgb, var(--color-teal-wave) 4%, transparent);
}
.incorrect-section {
  border-color: color-mix(in srgb, var(--color-coral) 30%, transparent);
  background: color-mix(in srgb, var(--color-coral) 4%, transparent);
}
.column-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.column-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--oa-text-secondary);
}
</style>
